import React, { useRef, useState } from 'react';
import {
    Box, Typography, Grid, Card, CardContent, Button,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Dialog, DialogTitle, DialogActions, DialogContent, Tooltip
} from '@mui/material';
import {
    FileDownload as FileDownloadIcon,
    FileUpload as FileUploadIcon,
    ErrorOutline as ErrorIcon
} from '@mui/icons-material';
import { useSnackbar } from '../context/SnackbarContext';
import api from '../api/axiosInstance';
import { validateEmail } from '../utils/validation';

const robustDateParse = (dateStr) => {
    if (!dateStr) return { value: null, status: 'valid', original: '' };
    const original = dateStr.trim();
    const months = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

    // 1. NEAR-STANDARD: YYYY MM DD, YYYY-MM-DD, YYYY/MM/DD
    const yearFirstRegex = /^(\d{4})[\s\-/](\d{1,2})[\s\-/](\d{1,2})$/;
    const yearFirstMatch = original.match(yearFirstRegex);

    let year, month, day, isDrastic = false;

    if (yearFirstMatch) {
        [, year, month, day] = yearFirstMatch;
    } else {
        // 2. DRASTICALLY DIFFERENT
        const parts = original.split(/[\s\-/.]+/);
        const monthPart = parts.find(p => months.includes(p.toLowerCase().substring(0, 3)));

        if (monthPart) {
            // FIX: Get the index from the 'months' array, not the 'parts' array
            const realMonthNum = months.indexOf(monthPart.toLowerCase().substring(0, 3)) + 1;
            month = realMonthNum.toString().padStart(2, '0');

            const otherParts = parts.filter(p => p !== monthPart);
            if (otherParts[0]?.length === 4) { [year, day] = otherParts; }
            else { [day, year] = otherParts; }
            isDrastic = true;
        } else if (parts.length === 3) {
            if (parts[2].length === 4) { [day, month, year] = parts; isDrastic = true; }
            else if (parts[0].length === 2) {
                [year, month, day] = parts;
                year = `20${year}`;
                isDrastic = true;
            }
        }
    }

    if (year && month && day) {
        const cleaned = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        const dObj = new Date(cleaned);

        // STRICT VALIDATION: Ensure JS didn't "auto-correct" Feb 31 to March 3
        const isRealDate = dObj.getFullYear() === parseInt(year) &&
            (dObj.getMonth() + 1) === parseInt(month) &&
            dObj.getDate() === parseInt(day);

        if (isRealDate) {
            return { value: cleaned, status: isDrastic ? 'inconsistent' : 'valid', original };
        }
    }

    // 3. HARD FAILURE (Impossible date or unparseable)
    return { value: original, status: 'invalid', original };
};



const WWCCSettings = () => {
    const fileInputRef = useRef(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [previewData, setPreviewData] = useState(null);
    const showSnackbar = useSnackbar();
    const dateFixes = previewData?.filter(r => r._meta.birthdate.status === 'inconsistent' || r._meta.expiry_date.status === 'inconsistent') || [];
    const blockingErrors = previewData?.filter(r =>
        r._meta.birthdate.status === 'invalid' ||
        r._meta.expiry_date.status === 'invalid' ||
        !r["Surname"] || !r["WWCC Number"]
    ) || [];
    const malformedEmails = previewData?.filter(r => !r._meta.email_valid) || [];
    const hasIssues = dateFixes.length > 0 || blockingErrors.length > 0 || malformedEmails.length > 0;

    const downloadTemplate = () => {
        const headers = [
            "First Name",
            "Other Given Names",
            "Surname",
            "Birthdate (yyyy/mm/dd)",
            "State",
            "WWCC Number",
            "Email",
            "Expiry Date",
            "Verified"
        ];
        const csvContent = "data:text/csv;charset=utf-8," + headers.join(",");
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", "wwcc_template.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // 1. CLEAR INPUT IMMEDIATELY
        // This allows the same file to be selected again if they cancel
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }

        setLoadingPreview(true);
        const reader = new FileReader();
        reader.onload = (event) => {
            setTimeout(() => {
                try {
                    const text = event.target.result;
                    const rows = text.split('\n').map(row => row.split(','));
                    const headers = rows[0].map(h => h.trim());

                    const parsedData = rows.slice(1)
                        .filter(row => row.length === headers.length && row.some(cell => cell.trim() !== ''))
                        .map(row => {
                            let rawObj = {};
                            headers.forEach((header, i) => {
                                rawObj[header] = row[i] ? row[i].trim() : '';
                            });

                            // Enrich data with validation metadata
                            const bdayMeta = robustDateParse(rawObj["Birthdate (yyyy/mm/dd)"]);
                            const expiryMeta = robustDateParse(rawObj["Expiry Date"]);
                            const isEmailValid = validateEmail(rawObj["Email"]);
                            const rawVerified = rawObj["Verified"]?.toString().toLowerCase().trim();

                            return {
                                ...rawObj,
                                _meta: {
                                    birthdate: bdayMeta,
                                    expiry_date: expiryMeta,
                                    email_valid: isEmailValid,
                                    verified_bool: (rawVerified === 'true' || rawVerified === 'yes' || rawVerified === '1') ? true :
                                        (rawVerified === 'false' || rawVerified === 'no' || rawVerified === '0') ? false : null,
                                }
                            };
                        });

                    setPreviewData(parsedData);
                } catch (error) {
                    showSnackbar("Error parsing CSV.", "error");
                } finally {
                    setLoadingPreview(false);
                }
            }, 150);
        };
        reader.readAsText(file);
    };


    const handleImport = async () => {
        if (blockingErrors.length > 0) return;
        setLoadingPreview(true);
        try {
            const formattedData = previewData.map(row => ({
                first_given_name: row["First Name"],
                other_given_names: row["Other Given Names"] || "",
                surname: row["Surname"],
                birthdate: row._meta.birthdate.value,
                wwcc_number: row["WWCC Number"],
                // If the email is invalid, we send null to the DB so we don't save trash
                email: row._meta.email_valid ? row["Email"] : null,
                expiry_date: row._meta.expiry_date.value,
                au_state: row["State"],
                verified: row._meta.verified_bool,
                is_active: true
            }));

            await api.post('/wwcc/bulk', { records: formattedData });
            setPreviewData(null);
            showSnackbar(`Successfully imported ${formattedData.length} records!`);
        } catch (err) {
            showSnackbar("Import failed: " + (err.response?.data?.error || err.message), "error");
        } finally {
            setLoadingPreview(false);
        }
    };

    // Helper for cell styling
    const getCellProps = (meta) => {

        // 1. Hard Error: Red background (Blocks Import)
        if (meta.status === 'invalid') {
            return {
                sx: {
                    bgcolor: '#ffebee',
                    color: '#c62828',
                    border: '1px solid #ef9a9a'
                }
            };
        }

        // 2. Drastic Difference: Bold Blue (User Alert)
        if (meta.status === 'inconsistent') {
            return {
                sx: {
                    color: 'info.main',
                    fontWeight: 'bold',
                    cursor: 'help' // Visual hint for tooltip
                }
            };
        }

        // 3. Normal or minor separator fix: Standard Black
        return { sx: { color: 'text.primary' } };


        // if (meta.status === 'invalid') return { sx: { bgcolor: '#ffebee', color: '#c62828' } };
        // if (meta.status === 'inconsistent') return { sx: { color: 'info.main', fontWeight: 'bold' } };
        // return {};
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>WWCC Bulk Import</Typography>

            <Typography variant="body1" sx={{ mb: 4 }} color="text.secondary">
                Manage your database by downloading the required template or uploading bulk records via CSV.
            </Typography>

            <Grid container spacing={3} alignItems="stretch">
                {/* 1. Get CSV Template */}
                <Grid item size={5} md={6}>
                    <Card sx={{ boxShadow: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" gutterBottom>1. Get CSV Template</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Download the template to ensure your data format is correct.
                            </Typography>
                        </CardContent>
                        <Box sx={{ p: 2, pt: 0 }}>
                            <Button
                                variant="contained"
                                startIcon={<FileDownloadIcon />}
                                onClick={downloadTemplate}
                                fullWidth
                            >
                                Download Template
                            </Button>
                        </Box>
                    </Card>
                </Grid>

                {/* 2. Bulk Upload Records */}
                <Grid item size={5} md={6}>
                    <Card sx={{ boxShadow: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <CardContent sx={{ flexGrow: 1 }}>
                            <Typography variant="h6" gutterBottom>2. Bulk Upload Records</Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Upload a CSV to add multiple WWCC records at once.
                            </Typography>
                        </CardContent>
                        <Box sx={{ p: 2, pt: 0 }}>
                            <Button
                                variant="contained"
                                component="label"
                                startIcon={<FileUploadIcon />}
                                fullWidth
                                disabled={loadingPreview}
                            >
                                {loadingPreview ? "Processing..." : "Upload CSV File"}
                                <input
                                    type="file"
                                    hidden
                                    accept=".csv"
                                    onChange={handleFileChange}
                                    ref={fileInputRef}
                                />
                            </Button>
                        </Box>
                    </Card>
                </Grid>
            </Grid>

            {/* CSV Preview Modal */}
            <Dialog
                open={Boolean(previewData)}
                onClose={() => setPreviewData(null)}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle sx={{ bgcolor: 'success.main', color: 'white' }}>
                    Confirm Bulk Upload {!hasIssues && `(${previewData?.length} Records)`}
                </DialogTitle>

                <DialogContent sx={{ mt: 2 }}>
                    {/* --- Health-check stats --- */}
                    {hasIssues && (
                        <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
                            {/* 1. Total Records - Always Show if there are issues */}
                            <Paper variant="outlined" sx={{ p: 1, flex: 1, textAlign: 'center', bgcolor: 'grey.100', borderLeft: '4px solid #444', borderBottom: '2px solid #444' }}>
                                <Typography variant="h5">{previewData?.length}</Typography>
                                <Typography variant="caption" color="text.secondary">TOTAL RECORDS</Typography>
                            </Paper>

                            {/* 2. Date Fixes - Only show if > 0 */}
                            {dateFixes.length > 0 && (
                                <Paper variant="outlined" sx={{ p: 1, flex: 1, textAlign: 'center', bgcolor: 'grey.100', borderLeft: '4px solid #0288d1', borderBottom: '2px solid #0288d1' }}>
                                    <Typography variant="h5" color="info.main">{dateFixes.length}</Typography>
                                    <Typography variant="caption" color="text.secondary">DATE FIXES</Typography>
                                </Paper>
                            )}

                            {/* 3. Malformed Emails - Only show if > 0 */}
                            {malformedEmails.length > 0 && (
                                <Paper variant="outlined" sx={{ p: 1, flex: 1, textAlign: 'center', bgcolor: 'grey.100', borderLeft: '4px solid #ed6c02', borderBottom: '2px solid #ed6c02' }}>
                                    <Typography variant="h5" color="warning.main">{malformedEmails.length}</Typography>
                                    <Typography variant="caption" color="text.secondary">MALFORMED EMAILS</Typography>
                                </Paper>
                            )}

                            {/* 4. Blocking Errors - Only show if > 0 */}
                            {blockingErrors.length > 0 && (
                                <Paper variant="outlined" sx={{ p: 1, flex: 1, textAlign: 'center', bgcolor: 'grey.100', borderLeft: '4px solid #d32f2f', borderBottom: '2px solid #d32f2f' }}>
                                    <Typography variant="h5" color="error.main">{blockingErrors.length}</Typography>
                                    <Typography variant="caption" color="text.secondary">BLOCKING ERRORS</Typography>
                                </Paper>
                            )}
                        </Box>
                    )}



                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 500 }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: 50, color: 'text.secondary', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Row #</TableCell>
                                    <TableCell>First Name</TableCell>
                                    <TableCell>Surname</TableCell>
                                    <TableCell>Birthdate</TableCell>
                                    <TableCell>WWCC Number</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Expiry Date</TableCell>
                                    <TableCell>Verified</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {previewData?.map((row, i) => (
                                    <TableRow key={i}>
                                        <TableCell sx={{
                                            color: 'text.secondary',
                                            bgcolor: 'grey.50',
                                            fontSize: '0.75rem',
                                            whiteSpace: 'nowrap'
                                        }}>
                                            {i + 2}
                                        </TableCell>
                                        <TableCell>{row["First Name"]}</TableCell>
                                        <TableCell sx={{ bgcolor: !row["Surname"] ? '#ffebee' : 'inherit' }}>
                                            {row["Surname"]}
                                        </TableCell>

                                        {/* BIRTHDATE CELL */}
                                        <Tooltip
                                            title={row._meta.birthdate.status === 'inconsistent' ? `Original: ${row._meta.birthdate.original}` : ""}
                                            placement="right"
                                            followCursor
                                        >
                                            <TableCell {...getCellProps(row._meta.birthdate)}>
                                                {row._meta.birthdate.value}
                                            </TableCell>
                                        </Tooltip>

                                        <TableCell><code>{row["WWCC Number"]}</code></TableCell>

                                        {/* EMAIL CELL */}
                                        <Tooltip
                                            title={!row._meta.email_valid ? "Invalid email format - will be imported as empty" : ""}
                                            placement="right"
                                            followCursor
                                        >
                                            <TableCell sx={{ color: !row._meta.email_valid ? 'warning.main' : 'inherit' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                    {!row._meta.email_valid && <ErrorIcon sx={{ fontSize: 16, mr: 0.5 }} />}
                                                    {row["Email"]}
                                                </Box>
                                            </TableCell>
                                        </Tooltip>


                                        {/* EXPIRY CELL */}
                                        < Tooltip
                                            title={row._meta.expiry_date.status === 'inconsistent' ? `Original: ${row._meta.expiry_date.original}` : ""}
                                            placement="right"
                                            followCursor
                                        >
                                            <TableCell {...getCellProps(row._meta.expiry_date)}>
                                                {row._meta.expiry_date.value}
                                            </TableCell>
                                        </Tooltip>

                                        <TableCell>{String(row._meta.verified_bool)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>

                {/* <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setPreviewData(null)}>Cancel</Button>
                    <Button variant="contained" color="success" onClick={handleImport}>Confirm Import</Button>
                </DialogActions> */}

                <DialogActions sx={{ p: 2, bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                    <Button onClick={() => setPreviewData(null)} color="inherit">
                        Cancel
                    </Button>

                    <Tooltip title={blockingErrors.length > 0 ? "Please fix red highlighted errors before importing" : ""}>
                        <span> {/* Span is required for tooltip to work on disabled buttons */}
                            <Button
                                variant="contained"
                                color="success"
                                onClick={handleImport}
                                disabled={blockingErrors.length > 0 || loadingPreview}
                            >
                                {loadingPreview ? "Importing..." : `Confirm Import (${previewData?.length} Rows)`}
                            </Button>
                        </span>
                    </Tooltip>
                </DialogActions>
            </Dialog >
        </Box >
    );
};

export default WWCCSettings;